import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import dayjs from "@/lib/bizday";
import api, { apiErr } from "@/lib/api";
import { inr, fdt } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { StatusBadge } from "@/components/pawkt/StatusBadge";
import { VisitReasonPicker, reasonsError } from "@/components/pawkt/VisitReasons";
import { VaccinationRecord, vaccinationError, vaccinationPayload } from "@/components/pawkt/VaccinationRecord";
import { RecordVaccinationGiven } from "@/components/pawkt/VaccinationSchedule";
import { RecordIncompletePanel } from "@/components/pawkt/RecordIncompletePanel";

const VET_DONE = { completed: "Vet done", cancelled: "Vet cancelled", no_show: "Vet no-show", in_progress: "Vet in progress", scheduled: "Vet scheduled" };
const VetWorkBadge = ({ b, m = "" }) => (
  <div data-testid={`vet-work-${m}${b.id}`} title="Veterinary part of this mixed booking. The booking itself stays open for its other services."
    className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${b.vet_work_status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{VET_DONE[b.vet_work_status] || b.vet_work_status}</div>
);
import { PackageBalanceWidget } from "@/components/pawkt/PackageCoverage";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Minus, Trash2, CalendarDays, Receipt, PlayCircle, PawPrint, IndianRupee, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { ApprovalRequestButton } from "@/components/pawkt/ApprovalRequestButton";
import { BookingProductsPicker, productSubtotal } from "@/components/pawkt/BookingProductsPicker";
import { ContextPanel } from "@/components/pawkt/ContextPanel";
import { CustomerCreateModal } from "@/components/pawkt/CustomerCreateModal";
import { useRangeParams, apiQueryString, DrillDownBanner } from "@/lib/drilldown";
import { CustomerCombobox } from "@/components/pawkt/CustomerCombobox";
import { AssignRoomButton } from "@/components/pawkt/AssignRoomButton";
import { ReallocateButton } from "@/components/pawkt/ReallocateButton";
import { bookingFormErrors, normCategory, staffRequiredFor, needsRoom } from "@/lib/bookingRules";
import { HoursOverrideDialog, isHoursBlock } from "@/components/business/HoursOverrideDialog";
import { StaffPlanButton } from "@/components/pawkt/StaffPlan";
import { AddServiceButton, UnbilledChip } from "@/components/bookings/AddServiceDialog";
import { ServiceStaffRows, serviceLines } from "@/components/pawkt/ServiceStaffRows";
import { VetAssignmentAlert } from "@/components/pawkt/VetAssignmentAlert";
import { RescheduleButton } from "@/components/pawkt/RescheduleButton";
import { BookingDateBar } from "@/components/bookings/BookingDateBar";
import { BookingSummary } from "@/components/bookings/BookingSummary";
import { ArrivedButton, useArrivalMap } from "@/components/visits/ArrivalDialog";

export const deriveVertical = (b) => {
  const s = `${b?.booking_type || ""} ${b?.service_name || ""}`.toLowerCase();
  if (s.includes("vet")) return "vet";
  if (s.includes("groom")) return "grooming";
  if (s.includes("board")) return "boarding";
  if (s.includes("train")) return "training";
  if (s.includes("daycare")) return "daycare";
  return "reception";
};

const STATUSES = ["pending", "confirmed", "in_progress", "completed", "cancelled", "no_show"];

function BillAction({ b, navigate, compact }) {
  const bill = b.billing || { state: "NOT_BILLED" };
  const billable = !["cancelled", "no_show"].includes(b.status);
  if (!billable) return null;                 // cancelled / no-show bookings: no billing action
  const go = () => navigate(`/pos?booking=${b.id}`);
  const cls = "h-8 px-2.5 text-xs gap-1";
  if (bill.state === "PAID") {
    return <Button size="sm" variant="outline" data-testid={`bill-view-${b.id}`} onClick={() => navigate("/invoices?status=paid")} className={`${cls} text-emerald-700 border-emerald-200`}><CheckCircle2 className="h-3.5 w-3.5" /> Paid</Button>;
  }
  if (bill.state === "UNPAID" || bill.state === "PARTIALLY_PAID") {
    return <Button size="sm" data-testid={`collect-${b.id}`} onClick={go} className={`${cls} bg-emerald-600 hover:bg-emerald-700 text-white`}><IndianRupee className="h-3.5 w-3.5" /> Collect {inr(bill.balance)}</Button>;
  }
  return <Button size="sm" data-testid={`bill-${b.id}`} onClick={go} className={`${cls} bg-emerald-600 hover:bg-emerald-700 text-white`}><Receipt className="h-3.5 w-3.5" /> Bill Booking</Button>;
}

function DepartmentBar({ counts, active, onPick }) {
  if (!counts) return null;
  const deps = counts.departments.filter((d) => d.bookings > 0 || d.key === active);
  const btn = (on) => `shrink-0 rounded-xl border px-3 py-2 text-left transition-colors ${on ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`;
  return (
    <div className="mb-4" data-testid="dept-bar">
      <div className="flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Departments">
        <button role="tab" aria-selected={!active} onClick={() => onPick("")} className={btn(!active)} data-testid="dept-tab-all">
          <div className="text-xs opacity-70">All departments</div><div className="text-lg font-bold tabular" data-testid="dept-count-all">{counts.unique_bookings}</div><div className="text-[10px] opacity-60">unique bookings</div>
        </button>
        {deps.map((d) => (
          <button key={d.key} role="tab" aria-selected={active === d.key} onClick={() => onPick(d.key)} className={btn(active === d.key)} data-testid={`dept-tab-${d.key}`}>
            <div className="text-xs opacity-70">{d.label}</div><div className="text-lg font-bold tabular" data-testid={`dept-count-${d.key}`}>{d.bookings}</div>
            <div className="text-[10px] opacity-60" data-testid={`dept-services-${d.key}`}>{d.services} service{d.services === 1 ? "" : "s"}</div>
          </button>))}
      </div>
      {counts.multi_department_bookings > 0 && <p className="mt-1 text-[11px] text-slate-400" data-testid="dept-note">{counts.multi_department_bookings} booking{counts.multi_department_bookings === 1 ? " includes" : "s include"} services from more than one department and appear under each; "All departments" counts each booking once.</p>}
    </div>
  );
}

export default function Bookings() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSettings = hasPerm(user, "settings.edit");
  const canCreate = hasPerm(user, "bookings.create");
  const [urlParams, setUrlParams] = useSearchParams();
  const [open, setOpen] = useState(() => urlParams.get("new") === "1");
  // Status filter is URL-driven (?status=completed from the dashboard); chips keep the URL in sync.
  const filter = STATUSES.includes(urlParams.get("status")) ? urlParams.get("status") : "";
  const setFilter = (s) => {
    const next = new URLSearchParams(urlParams);
    if (s) next.set("status", s); else next.delete("status");
    setUrlParams(next, { replace: true });
  };
  const [ctx, setCtx] = useState(null);
  const [search, setSearch] = useState(urlParams.get("search") || "");
  const [dsearch, setDsearch] = useState("");
  useEffect(() => { const t = setTimeout(() => setDsearch(search.trim()), 300); return () => clearTimeout(t); }, [search]);
  const range = useRangeParams();
  const basis = urlParams.get("basis") || "";
  const dept = urlParams.get("dept") || "";
  const setDept = (d) => { const next = new URLSearchParams(urlParams); if (d) next.set("dept", d); else next.delete("dept"); setUrlParams(next, { replace: true }); };
  const baseParams = () => {
    const params = {};
    if (filter) params.status = filter;
    if (dsearch) params.search = dsearch;
    if (range.period) {
      params.period = range.period;
      if (range.period === "custom") { params.start = range.start; params.end = range.end; }
      if (basis) params.date_basis = basis;
    }
    if (range.unassigned) params.unassigned = range.unassigned;
    return params;
  };
  const { data: deptCounts } = useQuery({
    queryKey: ["bookings", "dept-counts", filter, dsearch, range.period, range.start, range.end, range.unassigned, basis],
    queryFn: async () => (await api.get(`/bookings/department-counts${apiQueryString(baseParams())}`)).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["bookings", filter, dsearch, range.period, range.start, range.end, range.unassigned, basis, dept],
    queryFn: async () => {
      const params = baseParams();
      if (dept) params.department = dept;
      return (await api.get(`/bookings${apiQueryString(params)}`)).data;
    },
  });
  const { data: custs } = useQuery({ queryKey: ["customers-lite"], queryFn: async () => (await api.get("/customers?limit=200")).data });
  const arrived = useArrivalMap(data?.items);
  const { data: services } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  const { data: staff } = useQuery({ queryKey: ["staff"], queryFn: async () => (await api.get("/staff")).data });
  const activeStaff = (staff || []).filter((s) => s.active !== false);
  const assignPerformer = useMutation({
    mutationFn: ({ id, staff_id }) => api.patch(`/bookings/${id}/assign-performer`, { staff_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["vet-assignment-issues"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["staff-perf"] });
      toast.success("Performer assigned");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const [vaxCtx, setVaxCtx] = useState(null);
  const setStatus = useMutation({ mutationFn: ({ id, status }) => api.patch(`/bookings/${id}/status`, { status }), onSuccess: async (_r, { id, status }) => { qc.invalidateQueries({ queryKey: ["bookings"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); qc.invalidateQueries({ queryKey: ["vet-assignment-issues"] }); toast.success("Updated");
    if (status === "completed") { try { const c = (await api.get(`/bookings/${id}/vaccination-context`)).data; if (c.is_vaccination && c.pets.length) setVaxCtx(c); } catch { /* prompt is optional */ } } }, onError: (e) => toast.error(apiErr(e)) });

  const startSvc = useMutation({
    mutationFn: (id) => api.post(`/bookings/${id}/start-service`),
    onSuccess: (r) => {
      const d = r.data;
      qc.invalidateQueries({ queryKey: ["bookings"] });
      toast.success(d.existing ? "Opening service record" : d.needs_setup ? "Set up the service" : "Service started");
      if (d.route) navigate(d.route);
    },
    onError: (e) => {
      const msg = apiErr(e);
      if (/module not enabled/i.test(msg)) {
        toast.error("This module is disabled for your business.", canSettings ? { action: { label: "Enable in Settings", onClick: () => navigate("/settings") } } : { description: "Ask an admin to enable it in Settings → Modules." });
      } else {
        toast.error(msg);
      }
    },
  });

  return (
    <div>
      <PageHeader title="Bookings" subtitle="Appointments across services & staff">
        {canCreate && <Button onClick={() => setOpen(true)} data-testid="add-booking-btn" className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"><Plus className="h-4 w-4" /> New Booking</Button>}
      </PageHeader>
      {!basis && <DrillDownBanner metric="bookings" count={data?.total} />}
      <VetAssignmentAlert />
      <BookingDateBar />
      {basis && <BookingSummary period={range.period} start={range.start} end={range.end} basis={basis} />}
      <DepartmentBar counts={deptCounts} active={dept} onPick={setDept} />

      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        <FilterChip active={filter === ""} onClick={() => setFilter("")}>All</FilterChip>
        {STATUSES.map((s) => <FilterChip key={s} active={filter === s} onClick={() => setFilter(s)}>{s.replace("_", " ")}</FilterChip>)}
      </div>
      <div className="relative mb-4 max-w-sm">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by customer name or mobile…" data-testid="bookings-search" className="bg-white pr-8" />
        {search && <button onClick={() => setSearch("")} data-testid="bookings-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>}
      </div>

      {isLoading ? <Loading /> : (data?.items?.length ? (
        <>
        <div className="sm:hidden space-y-2.5" data-testid="bookings-cards">
          {data.items.map((b) => (
            <div key={b.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`booking-card-${b.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{b.customer_name}</div>
                  <div className="text-xs text-slate-400 truncate">{b.pet_name || "—"} · {b.service_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{fdt(b.start_time)}{b.is_backdated && <span data-testid={`backdated-m-${b.id}`} className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">BACKDATED</span>}</div>
                </div>
                <div className="font-semibold tabular shrink-0">{inr(b.total)}</div>
              </div>
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                {range.unassigned && (
                  <Select onValueChange={(v) => assignPerformer.mutate({ id: b.id, staff_id: v })}>
                    <SelectTrigger className="h-8 flex-1 bg-amber-50 border-amber-200 text-xs" data-testid={`assign-performer-m-${b.id}`} disabled={assignPerformer.isPending && assignPerformer.variables?.id === b.id}>
                      <SelectValue placeholder={assignPerformer.isPending && assignPerformer.variables?.id === b.id ? "Saving…" : "Assign performer"} />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {activeStaff.length === 0 ? <div className="px-3 py-2 text-xs text-slate-400">No active staff</div> : activeStaff.map((s) => <SelectItem key={s.id} value={s.id} className="text-xs">{s.name} ({s.role})</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {b.vet_work_status && <VetWorkBadge b={b} m="m-" />}
                <Select value={b.status} onValueChange={(status) => setStatus.mutate({ id: b.id, status })}>
                  <SelectTrigger className="h-8 flex-1 bg-white text-xs" data-testid={`booking-status-m-${b.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
                {needsRoom(b) && <AssignRoomButton booking={b} compact />}
                <AddServiceButton booking={b} compact />
                <ArrivedButton booking={b} visit={arrived[b.id]} compact />
                <StaffPlanButton booking={b} compact />
                <RescheduleButton booking={b} compact />
                {!!b.allocations?.length && !needsRoom(b) && <ReallocateButton booking={b} compact />}
                <button title="View context" data-testid={`ctx-open-m-${b.id}`} onClick={() => setCtx({ customerId: b.customer_id, petId: b.pet_id, vertical: deriveVertical(b) })} className="p-2 rounded-md hover:bg-sky-50 text-sky-600"><PawPrint className="h-4 w-4" /></button>
                {!["cancelled", "no_show"].includes(b.status) && (
                  <button title={b.operational_id ? "Open service" : "Start service"} data-testid={`start-service-m-${b.id}`} onClick={() => startSvc.mutate(b.id)} className="p-2 rounded-md text-sky-700 hover:bg-sky-50"><PlayCircle className="h-4 w-4" /></button>
                )}
                <BillAction b={b} navigate={navigate} compact />
                <UnbilledChip booking={b} />
                <ApprovalRequestButton action="bookings.delete" targetId={b.id} targetLabel={`${b.customer_name} · ${b.service_name}`} />
              </div>
            </div>
          ))}
        </div>
        <div className="hidden sm:block rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="bookings-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Customer / Pet</th><th className="px-4 py-3">Service</th><th className="px-4 py-3 hidden md:table-cell">Staff</th><th className="px-4 py-3">When</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((b) => (
                <tr key={b.id} className="hover:bg-slate-50" data-testid={`booking-${b.id}`}>
                  <td className="px-4 py-3"><div className="font-medium text-slate-900">{b.customer_name}</div><div className="text-xs text-slate-400">{b.pet_name || "—"}</div></td>
                  <td className="px-4 py-3 text-slate-700">{b.service_name}</td>
                  <td className="px-4 py-3 text-slate-600 hidden md:table-cell">
                    {range.unassigned ? (
                      <Select onValueChange={(v) => assignPerformer.mutate({ id: b.id, staff_id: v })}>
                        <SelectTrigger className="h-8 w-44 bg-amber-50 border-amber-200 text-xs" data-testid={`assign-performer-${b.id}`} disabled={assignPerformer.isPending && assignPerformer.variables?.id === b.id}>
                          <SelectValue placeholder={assignPerformer.isPending && assignPerformer.variables?.id === b.id ? "Saving…" : "Assign performer"} />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {activeStaff.length === 0 ? <div className="px-3 py-2 text-xs text-slate-400">No active staff</div> : activeStaff.map((s) => <SelectItem key={s.id} value={s.id} className="text-xs">{s.name} ({s.role})</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : <StaffCell b={b} />}
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fdt(b.start_time)}{b.is_backdated && <span data-testid={`backdated-${b.id}`} className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">BACKDATED</span>}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular">{inr(b.total)}<div><UnbilledChip booking={b} /></div></td>
                  <td className="px-4 py-3">
                    <Select value={b.status} onValueChange={(status) => setStatus.mutate({ id: b.id, status })}>
                      <SelectTrigger className="h-8 w-32 bg-white text-xs" data-testid={`booking-status-${b.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                    {b.vet_work_status && <VetWorkBadge b={b} />}
                    {needsRoom(b) && <div className="mt-1"><AssignRoomButton booking={b} /></div>}
                    {!!b.allocations?.length && (
                      <div className="mt-1 space-y-1" data-testid={`booking-alloc-${b.id}`}>
                        {b.allocations.map((a) => <div key={a.stay_id} className="text-[11px] text-slate-500">{a.resource_name}: {a.pets.map((p) => p.name).join(", ")}</div>)}
                        {!needsRoom(b) && !["cancelled", "no_show", "completed"].includes(b.status) && <ReallocateButton booking={b} />}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button title="View context" data-testid={`ctx-open-${b.id}`} onClick={() => setCtx({ customerId: b.customer_id, petId: b.pet_id, vertical: deriveVertical(b) })} className="p-1.5 rounded-md hover:bg-sky-50 text-sky-600"><PawPrint className="h-4 w-4" /></button>
                      {!["cancelled", "no_show"].includes(b.status) && (
                        <button data-testid={`start-service-${b.id}`} onClick={() => startSvc.mutate(b.id)}
                          title={b.operational_id ? "Open service record" : "Start service"}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-sky-700 hover:bg-sky-50">
                          <PlayCircle className="h-4 w-4" /> {b.operational_id ? "Open" : "Start"}
                        </button>
                      )}
                      <ArrivedButton booking={b} visit={arrived[b.id]} />
                      <AddServiceButton booking={b} />
                      <StaffPlanButton booking={b} />
                      <RescheduleButton booking={b} />
                      <BillAction b={b} navigate={navigate} />
                      <ApprovalRequestButton action="bookings.delete" targetId={b.id} targetLabel={`${b.customer_name} · ${b.service_name}`} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      ) : <Empty icon={CalendarDays} title="No bookings" subtitle="Create a booking to see it here and on the calendar." />)}

      {open && canCreate && <BookingModal open={open} onOpenChange={(o) => { setOpen(o); if (!o && urlParams.get("new")) { urlParams.delete("new"); urlParams.delete("customer_id"); setUrlParams(urlParams, { replace: true }); } }} custs={custs?.items || []} services={services || []} staff={staff || []}
        defaultCustomerId={urlParams.get("customer_id") || ""}
        onCreated={() => { qc.invalidateQueries({ queryKey: ["bookings"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); qc.invalidateQueries({ queryKey: ["vet-assignment-issues"] }); setOpen(false); }} />}
      {ctx && <ContextPanel open={!!ctx} onOpenChange={(o) => !o && setCtx(null)} customerId={ctx.customerId} petId={ctx.petId} vertical={ctx.vertical} />}
      {vaxCtx && <RecordVaccinationGiven open={!!vaxCtx} onOpenChange={(o) => !o && setVaxCtx(null)} pets={vaxCtx.pets} booking={vaxCtx} />}
    </div>
  );
}

function StaffCell({ b }) {
  const rows = (b.assignments || []).filter((a) => a.status !== "cancelled");
  if (!rows.length) return b.staff_name || "—";
  return (
    <div className="space-y-0.5" data-testid={`booking-assignments-${b.id}`}>
      {rows.map((a) => <div key={a.id} className="text-[11px]"><span className="text-slate-700">{a.service_name}</span>: {(a.staff_names || []).join(", ") || <span className="text-amber-600">unassigned</span>} <span className="text-slate-400">{dayjs(a.start_time).format("h:mm A")}–{dayjs(a.end_time).format("h:mm A")}</span> <span data-testid={`svc-status-${a.id}`} className="rounded bg-slate-100 px-1 text-[10px] font-semibold text-slate-600">{svcStatus(a, b)}</span></div>)}
    </div>
  );
}

const svcStatus = (a, b) => {
  if (a.status === "scheduled" && b.status === "in_progress") return a.category === "vet" ? "Waiting for vet" : "Waiting";
  return { scheduled: "Scheduled", in_progress: "In progress", completed: "Completed" }[a.status] || a.status;
};

function FilterChip({ active, onClick, children }) {
  return <button onClick={onClick} className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium capitalize transition-colors ${active ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{children}</button>;
}

export function BookingModal({ open, onOpenChange, custs, services, staff, defaultDate, defaultCustomerId, onCreated }) {
  const MAX_QTY = 99;
  const DUR_CATS = ["boarding", "daycare"];
  const unitFor = (s) => { const c = (s?.category || "").toLowerCase(); return c === "boarding" ? "Nights" : c === "daycare" ? "Days" : ""; };
  const maxQtyFor = (s) => DUR_CATS.includes((s?.category || "").toLowerCase()) ? 365 : MAX_QTY;
  const [form, setForm] = useState({
    customer_id: defaultCustomerId || "", staff_id: "", booking_taken_by: "", resource_id: "",
    date: defaultDate || dayjs().format("YYYY-MM-DD"), time: "10:00", notes: "", discount: 0, backdate_reason: "", visit_reasons: [], branch_id: "",
  });
  const isBackdated = form.date < dayjs().format("YYYY-MM-DD");
  // per-pet service assignment: ordered selected pet ids + { petId: [{service_id, qty}] }
  const [selectedPets, setSelectedPets] = useState([]);
  const [petServices, setPetServices] = useState({});
  const [vax, setVax] = useState({});
  const [busy, setBusy] = useState(false);
  const [prodRows, setProdRows] = useState([]);
  const reqId = useRef(`bkreq_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  const { user: me } = useAuth();
  const canProducts = hasPerm(me, "inventory.view");
  const tenantWide = !(me?.branch_ids || []).length;
  const { data: branchRows } = useQuery({ queryKey: ["branches"], enabled: tenantWide, queryFn: async () => (await api.get("/branches")).data });
  const pickBranch = tenantWide && (branchRows || []).length > 1;
  const branchStaff = pickBranch ? staff.filter((s) => !s.branch_id || s.branch_id === form.branch_id)
    : tenantWide ? staff : staff.filter((s) => !s.branch_id || (me?.branch_ids || []).includes(s.branch_id));
  const set = (k, v) => { setForm((s) => ({ ...s, [k]: v })); setErrors((e) => e.filter((x) => x.field !== k)); };
  const [errors, setErrors] = useState([]);
  const [hoursPrompt, setHoursPrompt] = useState(null);
  const [incomplete, setIncomplete] = useState(null);
  const errFor = (f) => errors.find((x) => x.field === f)?.msg;
  const { data: reqCfg } = useQuery({ queryKey: ["booking-requirements"], queryFn: async () => (await api.get("/settings/booking-requirements")).data });
  const svcById = (id) => services.find((x) => x.id === id);
  const [svcStaff, setSvcStaff] = useState({});

  const qc = useQueryClient();
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [extraCusts, setExtraCusts] = useState([]);
  const allCusts = [...extraCusts, ...custs.filter((c) => !extraCusts.some((e) => e.id === c.id))];
  const onNewCustomer = (c) => {
    setExtraCusts((prev) => [c, ...prev.filter((x) => x.id !== c.id)]);
    set("customer_id", c.id);
    const petIds = (c.pets || []).map((p) => p.id);
    setSelectedPets(petIds);
    setPetServices(Object.fromEntries(petIds.map((id) => [id, []])));
    qc.invalidateQueries({ queryKey: ["cust-pets", c.id] });
    qc.invalidateQueries({ queryKey: ["customers-lite"] });
  };

  const { data: pets } = useQuery({
    queryKey: ["cust-pets", form.customer_id], enabled: !!form.customer_id,
    queryFn: async () => (await api.get(`/pets?customer_id=${form.customer_id}`)).data,
  });
  const petList = pets?.items || [];

  const onCustomer = (v) => { set("customer_id", v); setSelectedPets([]); setPetServices({}); setIncomplete(null); };
  const togglePet = (pid) => {
    setIncomplete(null);
    setSelectedPets((prev) => prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]);
    setPetServices((prev) => {
      if (prev[pid]) { const n = { ...prev }; delete n[pid]; return n; }
      return { ...prev, [pid]: [] };
    });
  };
  const addSvc = (pid, sid) => setPetServices((prev) => {
    const rows = prev[pid] || [];
    if (rows.some((r) => r.service_id === sid)) return prev;
    return { ...prev, [pid]: [...rows, { service_id: sid, qty: 1 }] };
  });
  const removeSvc = (pid, sid) => setPetServices((prev) => ({ ...prev, [pid]: (prev[pid] || []).filter((r) => r.service_id !== sid) }));
  const setQty = (pid, sid, q) => setPetServices((prev) => ({
    ...prev, [pid]: (prev[pid] || []).map((r) => r.service_id === sid ? { ...r, qty: Math.min(maxQtyFor(svcById(sid)), Math.max(1, Number.isFinite(+q) ? Math.round(+q) : 1)) } : r),
  }));

  const rowsFor = (pid) => petServices[pid] || [];
  const totalDuration = selectedPets.reduce((t, pid) => t + rowsFor(pid).reduce((d, r) => d + (svcById(r.service_id)?.duration || 0) * r.qty, 0), 0);
  const estTotal = selectedPets.reduce((t, pid) => t + rowsFor(pid).reduce((s, r) => s + (svcById(r.service_id)?.price || 0) * r.qty, 0), 0);
  const petName = (pid) => { const p = petList.find((x) => x.id === pid); return p ? p.name : "Pet"; };

  const svcCount = selectedPets.reduce((t, pid) => t + rowsFor(pid).length, 0);
  const productsOnly = svcCount === 0 && prodRows.length > 0;
  const prodTotal = productSubtotal(prodRows);
  const servicesOk = selectedPets.length > 0 && selectedPets.every((pid) => rowsFor(pid).length > 0);
  const canSubmit = form.customer_id && (productsOnly || servicesOk) && (!isBackdated || form.backdate_reason.trim());

  // PAWKT-BOARDING-RESOURCE — when a boarding service is in the cart, offer an available
  // resource for the whole nights window (server-authoritative availability).
  const hasBoarding = selectedPets.some((pid) => rowsFor(pid).some((r) => (svcById(r.service_id)?.category || "").toLowerCase() === "boarding"));
  const hasVet = selectedPets.some((pid) => rowsFor(pid).some((r) => normCategory(svcById(r.service_id)?.category) === "vet"));
  const boardNights = Math.max(1, ...[1, ...selectedPets.flatMap((pid) => rowsFor(pid).filter((r) => (svcById(r.service_id)?.category || "").toLowerCase() === "boarding").map((r) => r.qty))]);
  const bStart = dayjs(`${form.date}T${form.time}:00`).format("YYYY-MM-DDTHH:mm:ss+05:30");
  const bEnd = dayjs(`${form.date}T${form.time}:00`).add(boardNights, "day").format("YYYY-MM-DDTHH:mm:ss+05:30");
  const boardPets = selectedPets.filter((pid) => rowsFor(pid).some((r) => (svcById(r.service_id)?.category || "").toLowerCase() === "boarding"));
  const { data: avail } = useQuery({
    queryKey: ["boarding-avail", bStart, bEnd, hasBoarding, form.customer_id, boardPets.join(",")], enabled: hasBoarding && !!form.customer_id,
    queryFn: async () => (await api.get("/boarding/availability", { params: { start_time: bStart, end_time: bEnd, customer_id: form.customer_id, pet_ids: boardPets.join(",") } })).data,
  });
  const availRes = (avail?.items || []).filter((x) => x.available || x.id === form.resource_id);
  const blockedRes = (avail?.items || []).filter((x) => !x.available && x.reason && x.occupied > 0 && x.capacity > 1);

  const submit = async (overrideReason = "") => {
    const cartCats = selectedPets.flatMap((pid) => rowsFor(pid).map((r) => normCategory(svcById(r.service_id)?.category)));
    const errs = bookingFormErrors({
      customerId: form.customer_id, productsOnly, selectedPets,
      emptyPets: selectedPets.filter((pid) => rowsFor(pid).length === 0).map(petName),
      staffCats: staffRequiredFor(cartCats, reqCfg?.requirements), staffId: perService ? primaryStaff : form.staff_id,
      isBackdated, backdateReason: form.backdate_reason,
    });
    if (pickBranch && !form.branch_id) errs.unshift({ field: "branch_id", msg: "Choose the branch for this appointment." });
    setErrors(errs);
    const vetErr = hasVet ? reasonsError(form.visit_reasons || []) : "";
    if (vetErr) { setErrors((e) => [...e, { field: "visit_reasons", msg: vetErr }]); return; }
    const vaxPets = selectedPets.map((pid) => petList.find((p) => p.id === pid)).filter(Boolean);
    const vaxErr = !productsOnly ? vaccinationError(vaxPets, vax) : "";
    if (vaxErr) { setErrors((e) => [...e, { field: "vaccination_record", msg: vaxErr }]); return; }
    if (errs.length) {
      setTimeout(() => document.querySelector(`[data-field="${errs[0].field}"] input, [data-field="${errs[0].field}"] button`)?.focus(), 0);
      return;
    }
    setBusy(true);
    try {
      const start_time = dayjs(`${form.date}T${form.time}:00`).format("YYYY-MM-DDTHH:mm:ss+05:30");
      const products = prodRows.map((r) => ({ product_id: r.product_id, qty: r.qty }));
      const common = { customer_id: form.customer_id, booking_taken_by: form.booking_taken_by, start_time, notes: form.notes,
        backdate_reason: isBackdated ? form.backdate_reason.trim() : "", products, request_id: reqId.current,
        hours_override_reason: overrideReason, ...(pickBranch ? { branch_id: form.branch_id } : {}) };
      const warn = (r) => r?.data?.hours_warning && toast.warning(r.data.hours_warning.msg);
      if (productsOnly) {
        warn(await api.post("/bookings", { ...common, pet_id: selectedPets[0] || "", staff_id: form.staff_id }));
        toast.success("Booking created");
        onCreated();
        return;
      }
      const pet_services = selectedPets.map((pid) => ({
        pet_id: pid,
        service_ids: rowsFor(pid).map((r) => r.service_id),
        quantities: Object.fromEntries(rowsFor(pid).map((r) => [r.service_id, r.qty])),
      }));
      const created = await api.post("/bookings", {
        ...common, pet_id: selectedPets[0], pet_services,
        staff_id: perService ? primaryStaff : form.staff_id,
        ...(perService ? { service_staff: Object.fromEntries(svcLines.map((l) => [l.key, svcStaff[l.key] || ""])) } : {}),
        resource_id: hasBoarding ? (form.resource_id || "") : "",
        ...(hasVet ? { visit_reasons: form.visit_reasons } : {}),
        vaccination_record: vaccinationPayload(vaxPets, vax),
      });
      warn(created);
      if (created.data?.staff_warnings?.length) toast.warning(`Check staff timing: ${created.data.staff_warnings.length} overlap(s) with other bookings or time off.`);
      toast.success(created.data?.status === "pending" ? "Booking saved as Pending — assign a room to confirm it" : "Booking created");
      setHoursPrompt(null);
      onCreated();
    } catch (e) {
      if (isHoursBlock(e)) { setHoursPrompt(e.response.data.detail); return; }
      if (e?.response?.data?.detail?.code === "RECORD_INCOMPLETE") { setIncomplete(e.response.data.detail.missing); toast.error(e.response.data.detail.msg); return; }
      const f = e?.response?.data?.detail?.fields;
      if (Array.isArray(f) && f.length) setErrors(f.map((field) => ({ field, msg: apiErr(e) })));
      toast.error(e?.response?.status === 409 && perService ? "One of the chosen staff already has a booking at this time. Choose another person or time for that service." : apiErr(e));
    } finally { setBusy(false); }
  };

  const staffCatsNow = staffRequiredFor(selectedPets.flatMap((pid) => rowsFor(pid).map((r) => normCategory(svcById(r.service_id)?.category))), reqCfg?.requirements);
  const svcLines = productsOnly ? [] : serviceLines(selectedPets, rowsFor, svcById, petName);
  const perService = svcLines.length > 1;
  const primaryStaff = svcLines.map((l) => svcStaff[l.key]).find(Boolean) || "";

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]" data-testid="booking-modal">
        <DialogHeader><DialogTitle className="font-display text-xl">New Booking</DialogTitle><DialogDescription className="sr-only">Choose a customer, pets, services or products, and a date and time.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {errors.length > 0 && (
            <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700" data-testid="bk-errors">
              <div className="font-semibold mb-1">Complete these before saving:</div>
              <ul className="list-disc pl-4 space-y-0.5">{errors.map((x) => <li key={x.field}>{x.msg}</li>)}</ul>
            </div>
          )}
          <div className="space-y-1.5">
            <CustomerCombobox value={form.customer_id} options={allCusts} onSelect={onCustomer} error={errFor("customer_id")} />
          {hasBoarding && (
            <div className="space-y-1.5" data-testid="bk-resource-section" data-field="resource_id">
              <label className="text-xs font-medium text-slate-600">Boarding Resource · {boardNights} night{boardNights > 1 ? "s" : ""}</label>
              <select data-testid="bk-resource" value={form.resource_id} onChange={(e) => set("resource_id", e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
                <option value="">Assign later (saved as Pending)</option>
                {availRes.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.type} · {r.occupied}/{r.capacity} pets</option>)}
              </select>
              {boardPets.length > 0 && (
                <p className="text-xs text-slate-600" data-testid="bk-resource-pets">
                  {form.resource_id ? "Allocating to this unit: " : "Pets needing a unit: "}{boardPets.map(petName).join(", ")}
                  {boardPets.length > 1 && " — only units with room for all of them are listed."}
                </p>
              )}
              {blockedRes.length > 0 && <p className="text-[11px] text-slate-400" data-testid="bk-resource-blocked">Not offered: {blockedRes.slice(0, 3).map((r) => r.reason).join(" ")}</p>}
              {!form.resource_id && <p className="text-xs text-amber-700" data-testid="bk-pending-note">Without a room this booking is saved as Pending. It can be confirmed once a room/kennel is assigned.</p>}
              {errFor("resource_id") && <p className="text-xs text-rose-600" data-testid="bk-err-resource_id">{errFor("resource_id")}</p>}
            </div>
          )}
            <button type="button" onClick={() => setNewCustOpen(true)} data-testid="bk-new-customer-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-pawkt-primary hover:underline">
              <Plus className="h-3.5 w-3.5" /> New Customer
            </button>
          </div>

          {form.customer_id && <PackageBalanceWidget customerId={form.customer_id} title="Package coverage available" compact />}

          {form.customer_id && (
            <div data-testid="bk-pets-picker" data-field="pets">
              <Label className="text-xs text-slate-500 mb-1.5 block">Pets {selectedPets.length > 0 && <span className="text-slate-400">· {selectedPets.length} selected</span>}</Label>
              {petList.length === 0 ? (
                <div className="rounded-xl border border-dashed border-pawkt-border bg-white p-4 text-center text-sm text-slate-500" data-testid="bk-no-pets">
                  This customer has no pets yet. Add a pet from their profile first.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {petList.map((p) => {
                    const on = selectedPets.includes(p.id);
                    return (
                      <button key={p.id} type="button" onClick={() => togglePet(p.id)} data-testid={`bk-pet-toggle-${p.id}`} aria-pressed={on}
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium min-h-[44px] transition-colors ${on ? "border-pawkt-primary bg-pawkt-primary-soft text-pawkt-primary" : "border-pawkt-border bg-white text-slate-700 hover:border-pawkt-primary"}`}>
                        <span className={`h-4 w-4 rounded-full border flex items-center justify-center shrink-0 ${on ? "bg-pawkt-primary border-pawkt-primary text-white" : "border-slate-300"}`}>{on && <CheckCircle2 className="h-3 w-3" />}</span>
                        <PawPrint className="h-3.5 w-3.5" /> {p.name}
                        <span className="text-xs text-slate-400">{p.breed || p.species || ""}{p.pet_code ? ` · ${p.pet_code}` : ""}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {errFor("pets") && <p className="mt-1 text-xs text-rose-600" data-testid="bk-err-pets">{errFor("pets")}</p>}
            </div>
          )}

          {selectedPets.map((pid) => {
            const rows = rowsFor(pid);
            const available = services.filter((s) => s.active !== false && !rows.some((r) => r.service_id === s.id));
            return (
              <div key={pid} className="rounded-[18px] border border-pawkt-border bg-pawkt-bg p-3" data-testid={`bk-pet-section-${pid}`} data-field={rows.length === 0 ? "services" : undefined}>
                <div className="flex items-center justify-between mb-2">
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><PawPrint className="h-4 w-4 text-pawkt-primary" /> {petName(pid)}</span>
                  <button type="button" onClick={() => togglePet(pid)} data-testid={`bk-pet-remove-${pid}`} aria-label={`Remove ${petName(pid)}`} className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50"><X className="h-4 w-4" /></button>
                </div>
                {rows.length > 0 && (
                  <div className="space-y-1.5 mb-2">
                    {rows.map((r) => { const s = svcById(r.service_id); if (!s) return null; const unit = unitFor(s); const maxq = maxQtyFor(s); return (
                      <div key={r.service_id} className="flex items-center justify-between gap-2 rounded-xl border border-pawkt-border bg-white px-2.5 py-2" data-testid={`bk-pet-svc-${pid}-${r.service_id}`}>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">{s.name} <span className="text-xs text-slate-400">· {inr(s.price)}</span></span>
                        {unit && <span className="text-xs font-semibold text-pawkt-primary shrink-0">{unit}</span>}
                        <div className="flex items-center rounded-lg border border-pawkt-border bg-white shrink-0">
                          <button type="button" onClick={() => setQty(pid, r.service_id, r.qty - 1)} disabled={r.qty <= 1} data-testid={`bk-qty-dec-${pid}-${r.service_id}`} aria-label="Decrease quantity" className="h-9 w-9 flex items-center justify-center text-slate-600 disabled:text-slate-300"><Minus className="h-3.5 w-3.5" /></button>
                          {unit
                            ? <input type="number" min={1} max={maxq} value={r.qty} onChange={(e) => setQty(pid, r.service_id, e.target.value)} data-testid={`bk-qty-val-${pid}-${r.service_id}`} className="w-12 text-center text-sm font-semibold tabular outline-none bg-white" aria-label={`${unit} for ${s.name}`} />
                            : <span data-testid={`bk-qty-val-${pid}-${r.service_id}`} className="min-w-[1.75rem] text-center text-sm font-semibold tabular">{r.qty}</span>}
                          <button type="button" onClick={() => setQty(pid, r.service_id, r.qty + 1)} disabled={r.qty >= maxq} data-testid={`bk-qty-inc-${pid}-${r.service_id}`} aria-label="Increase quantity" className="h-9 w-9 flex items-center justify-center text-slate-600 disabled:text-slate-300"><Plus className="h-3.5 w-3.5" /></button>
                        </div>
                        <button type="button" onClick={() => removeSvc(pid, r.service_id)} data-testid={`bk-pet-svc-remove-${pid}-${r.service_id}`} aria-label={`Remove ${s.name}`} className="h-9 w-9 shrink-0 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ); })}
                  </div>
                )}
                <Select value="" onValueChange={(v) => v && addSvc(pid, v)}>
                  <SelectTrigger className="bg-white h-11" data-testid={`bk-add-svc-${pid}`}><SelectValue placeholder="+ Add service" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {available.length === 0 ? <div className="px-2 py-3 text-xs text-slate-400">No more services</div>
                      : available.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {inr(s.price)} · {s.duration}min</SelectItem>)}
                  </SelectContent>
                </Select>
                {rows.length === 0 && !productsOnly && <p className="mt-1.5 text-xs text-rose-600" data-testid={`bk-pet-empty-${pid}`}>Add at least one service for {petName(pid)}, or add products only.</p>}
              </div>
            );
          })}

          {hasVet && <div className="rounded-[18px] border border-sky-200 bg-sky-50/40 p-3" data-testid="bk-vet-reasons"><VisitReasonPicker value={form.visit_reasons} onChange={(v) => set("visit_reasons", v)} error={errFor("visit_reasons")} /></div>}
          {!productsOnly && selectedPets.length > 0 && <VaccinationRecord pets={selectedPets.map((pid) => petList.find((p) => p.id === pid)).filter(Boolean)} value={vax} onChange={setVax} error={errFor("vaccination_record")} />}
          <div className="grid sm:grid-cols-2 gap-3">
            {pickBranch && (
              <div className="sm:col-span-2">
                <Sel label="Branch *" value={form.branch_id} onChange={(v) => { set("branch_id", v); const s = staff.find((x) => x.id === form.staff_id); if (s?.branch_id && s.branch_id !== v) set("staff_id", ""); }}
                  options={branchRows.map((br) => ({ value: br.id, label: br.name }))} testid="bk-branch" placeholder="Choose branch first" field="branch_id" error={errFor("branch_id")} full />
              </div>
            )}
            {perService ? <ServiceStaffRows lines={svcLines} date={form.date} time={form.time} branchId={form.branch_id} value={svcStaff} onChange={setSvcStaff} />
              : <Sel label={staffCatsNow.length ? "Service Staff *" : "Service Staff"} value={form.staff_id} onChange={(v) => set("staff_id", v)} options={branchStaff.map((s) => ({ value: s.id, label: `${s.name} (${s.role})` }))} testid="bk-staff" placeholder={pickBranch && !form.branch_id ? "Choose branch first" : staffCatsNow.length ? `Required for ${staffCatsNow.join(", ")}` : "Who performs it (optional)"} field="staff_id" error={errFor("staff_id")} />}
            <Sel label="Booking Taken By" value={form.booking_taken_by} onChange={(v) => set("booking_taken_by", v)} options={staff.map((s) => ({ value: s.id, label: `${s.name} (${s.role})` }))} testid="bk-taken-by" placeholder="Who took it (optional)" />
            <div><Label className="text-xs text-slate-500 mb-1 block">Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="bk-date" className="bg-white h-11" /></div>
            <div><Label className="text-xs text-slate-500 mb-1 block">Time</Label><Input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} data-testid="bk-time" className="bg-white h-11" /></div>
            <div><Label className="text-xs text-slate-500 mb-1 block">Discount (₹)</Label><Input type="number" value={form.discount} onChange={(e) => set("discount", e.target.value)} className="bg-white" /></div>
          </div>

          {isBackdated && (
            <div className="rounded-[18px] border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="bk-backdate-notice" data-field="backdate_reason">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-800"><CalendarDays className="h-4 w-4" /> Backdated appointment</div>
              <p className="text-xs text-amber-700">You're creating this appointment for a past date. Pawkt records it on {dayjs(form.date).format("DD MMM YYYY")} and also notes when it was entered.</p>
              <div>
                <Label className="text-xs text-amber-800 mb-1 block">Reason for backdating *</Label>
                <Input value={form.backdate_reason} onChange={(e) => set("backdate_reason", e.target.value)} data-testid="bk-backdate-reason" placeholder="e.g. Visit happened yesterday but wasn't entered" className="bg-white h-11" />
                {errFor("backdate_reason") && <p className="text-xs text-rose-600" data-testid="bk-err-backdate_reason">{errFor("backdate_reason")}</p>}
              </div>
            </div>
          )}

          {canProducts && form.customer_id && <BookingProductsPicker rows={prodRows} setRows={setProdRows} />}

          {(estTotal > 0 || prodRows.length > 0) && (
            <div className="rounded-[18px] border border-pawkt-border bg-pawkt-bg p-3 text-sm" data-testid="bk-appointment-summary">
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Appointment summary</div>
              {selectedPets.map((pid) => rowsFor(pid).length > 0 && (
                <div key={pid} className="mb-1.5">
                  <div className="text-xs font-semibold text-slate-700">{petName(pid)}</div>
                  {rowsFor(pid).map((r) => { const s = svcById(r.service_id); if (!s) return null; return (
                    <div key={r.service_id} className="flex items-center justify-between text-slate-600">
                      <span>{s.name} ×{r.qty}</span><span className="tabular">{inr(s.price * r.qty)}</span>
                    </div>
                  ); })}
                </div>
              ))}
              {prodRows.length > 0 && (
                <div className="mb-1.5" data-testid="bk-summary-products">
                  <div className="text-xs font-semibold text-slate-700">Products</div>
                  {prodRows.map((r) => (
                    <div key={r.product_id} className="flex items-center justify-between text-slate-600">
                      <span>{r.name} ×{r.qty}</span><span className="tabular">{inr(r.price * r.qty)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-1 space-y-0.5 border-t border-pawkt-border pt-1.5 text-xs text-slate-500">
                <div className="flex justify-between"><span>Services</span><span className="tabular" data-testid="bk-services-subtotal">{inr(estTotal)}</span></div>
                <div className="flex justify-between"><span>Products</span><span className="tabular" data-testid="bk-products-subtotal">{inr(prodTotal)}</span></div>
              </div>
              <div className="mt-1 flex items-center justify-between border-t border-pawkt-border pt-1.5 font-semibold text-slate-900">
                <span>Estimated total <span className="text-xs font-normal text-slate-400">· {productsOnly ? 15 : totalDuration} min</span></span>
                <span className="tabular" data-testid="bk-estimated-total">{inr(estTotal + prodTotal)}</span>
              </div>
              <p className="mt-1 text-[11px] text-slate-400">Estimate only — final pricing (membership, packages, coupons, tax) is calculated at billing. Stock is deducted only when billed.</p>
            </div>
          )}
        </div>
        {incomplete?.length > 0 && <RecordIncompletePanel missing={incomplete} customerId={form.customer_id} petIds={selectedPets} onResolved={setIncomplete} />}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => submit()} disabled={busy || !canSubmit || incomplete?.length > 0} data-testid="bk-submit" className="h-11 bg-slate-900 text-white">{busy ? "Booking…" : "Create booking"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <HoursOverrideDialog key={hoursPrompt ? "open" : "closed"} prompt={hoursPrompt} busy={busy} onCancel={() => setHoursPrompt(null)} onConfirm={(r) => submit(r)} />
    <CustomerCreateModal open={newCustOpen} onOpenChange={setNewCustOpen} onCreated={onNewCustomer}
      title="New Customer" submitLabel="Create & select" testid="booking-customer-modal" />
    </>
  );
}

function Sel({ label, value, onChange, options, testid, full, placeholder, field, error }) {
  return (
    <div className={full ? "sm:col-span-2" : ""} data-field={field}>
      <Label className="text-xs text-slate-500 mb-1 block">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={`bg-white ${error ? "border-rose-400" : ""}`} data-testid={testid} aria-invalid={!!error}><SelectValue placeholder={placeholder || "Select"} /></SelectTrigger>
        <SelectContent className="bg-white max-h-64">{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
      {error && <p className="mt-1 text-xs text-rose-600" data-testid={`bk-err-${field}`}>{error}</p>}
    </div>
  );
}
